import React, { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu as MuiMenu,
  MenuItem,
  Collapse,
} from "@mui/material";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  LayoutDashboard,
  Settings,
  FileVolume2,
  Tickets,
  PhoneCall,
  SquareKanban,
  ContactRound,
  ChevronDown,
  ChevronUp,
  BarChart3, // Icon for Reports
  FileText, // Icon for sub-reports
  Calendar,
  Users,
  User,
  LogOut,
  Facebook,
} from "lucide-react";

import ProfileImg from "../../src/assets/user.png";
import Logo from "../../src/assets/just-talk-logo.png";

const DRAWER_WIDTH = 280;

export default function SideNavbar({ data }) {
  const name = localStorage.getItem("username") || "Workspace";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // State for collapsible menus
  const [openReports, setOpenReports] = useState(false);

  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isLinkActive = (path) => {
    const currentFull = location.pathname === "/lead-management-system" && !location.search
      ? "/lead-management-system?pipeline=meta"
      : location.pathname + location.search;
    return currentFull === path || location.pathname === path;
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    navigate("/");
    localStorage.clear();
  };

  const navigationItems = [
    {
      text: "Dashboard",
      icon: <LayoutDashboard size={20} />,
      path: "/dashboard",
    },
    { text: "Solution FAQ’s", icon: <FileVolume2 size={20} />, path: "/faq" },
    { text: "Call Log", icon: <PhoneCall size={20} />, path: "/call-log" },
    { text: "Tickets", icon: <Tickets size={20} />, path: "/all-tickets" },
    {
      text: "Lead Status",
      icon: <ContactRound size={20} />,
      path: "/new-lead",
    },
    {
      text: "Lead Pipeline",
      icon: <SquareKanban size={20} />,
      path: "/lead-management-system?pipeline=meta",
    },
    {
      text: "WhatsApp Board",
      icon: <SquareKanban size={20} />,
      path: "/lead-management-system?pipeline=whatsapp",
    },
    {
      text: "Appointment Booking",
      icon: <Calendar size={20} />,
      path: "/appointment-booking",
    },
    {
      text: "Meta Leads",
      icon: <Facebook size={20} />,
      path: "/MetaLeads",
    },
    {
      text: "Team Members",
      icon: <Users size={20} />,
      path: "/team-members",
    },
  ];

  const reportSubItems = [
    { text: "Traffic", path: "/reports/traffic" },
    { text: "CDR Report", path: "/reports/cdr" },
    { text: "SLA Report", path: "/reports/sla" },
    { text: "Conversion Report", path: "/reports/conversion" },
    { text: "Unreached Leads", path: "/reports/unreached" },
  ];

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        color: "black",
        borderRight: "1px solid #eee",
      }}
    >
      {/* Logo */}
      <Box
        sx={{ p: "15px 25px 10px 22px", display: "flex", alignItems: "center" }}
      >
        <img
          src={Logo}
          style={{ height: 40, mixBlendMode: "hard" }}
          alt="Logo"
        />
      </Box>

      {/* Workspace Selector */}
      <Box sx={{ px: 3, mb: 3 }}>
        <Button
          fullWidth
          sx={{
            justifyContent: "space-between",
            color: "white",
            bgcolor: "rgba(255, 255, 255, 0.04)",
            borderRadius: 3,
            border: "1px solid #4B4B4B",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
          }}
        >
          <Box sx={{ textAlign: "left", p: "1px 5px" }}>
            <Typography
              variant="body2"
              color="rgba(22, 22, 22, 0.7)"
              sx={{ textTransform: "capitalize", fontWeight: 100 }}
            >
              {name}
            </Typography>
          </Box>
        </Button>
      </Box>

      <Box sx={{ borderTop: "0.5px solid #ccc", p: "5px 0 0px 0" }}></Box>

      {/* Navigation Items */}
      <List sx={{ px: 2, flex: 1 }}>
        {navigationItems.map((item) => {
          const active = isLinkActive(item.path);
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={NavLink}
                to={item.path}
                sx={{
                  borderRadius: 3,
                  bgcolor: active ? "rgb(99, 102, 241) !important" : "transparent",
                  "& .MuiListItemText-primary": {
                    color: active ? "white !important" : "black",
                    fontWeight: active ? 600 : 100,
                  },
                  "& .MuiListItemIcon-root": {
                    color: active ? "white !important" : "grey",
                  },
                  "&:hover": {
                    bgcolor: active ? "rgb(99, 102, 241)" : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}

        {/* --- REPORTS COLLAPSIBLE SECTION --- */}
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            onClick={() => setOpenReports(!openReports)}
            sx={{
              borderRadius: 3,
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
              <Box sx={{ display: 'flex' }}><BarChart3 size={20} /></Box>
            </ListItemIcon>
            <ListItemText
              primary="Reports"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 100,
                  color: "black",
                },
              }}
            />
            {openReports ? (
              <ChevronUp size={18} color="grey" />
            ) : (
              <ChevronDown size={18} color="grey" />
            )}
          </ListItemButton>

          <Collapse in={openReports} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 2 }}>
              {reportSubItems.map((subItem) => (
                <ListItemButton
                  key={subItem.text}
                  component={NavLink}
                  to={subItem.path}
                  sx={{
                    borderRadius: 3,
                    mt: 0.5,
                    "&.active": {
                      bgcolor: "rgba(99, 102, 241, 0.1)", // Light purple highlight
                      "& .MuiListItemText-primary": {
                        color: "rgb(99, 102, 241)",
                        fontWeight: 600,
                      },
                    },
                    "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <ListItemText
                    primary={subItem.text}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "0.8rem",
                        fontWeight: 100,
                        color: "black",
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </ListItem>
      </List>

      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ borderTop: "0.5px solid #ccc", my: 1 }}></Box>
        <List disablePadding>
          {/* User Profile */}
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/profile"
              sx={{
                borderRadius: 3,
                "&.active": {
                  bgcolor: "rgb(99, 102, 241)",
                  "& .MuiListItemText-primary": {
                    color: "white",
                    fontWeight: 600,
                  },
                  "& .MuiListItemIcon-root": { color: "white" },
                  "&:hover": { bgcolor: "rgb(99, 102, 241)" },
                },
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
                <Box sx={{ display: 'flex' }}><User size={20} /></Box>
              </ListItemIcon>
              <ListItemText
                primary="User Profile"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 100,
                    color: "black",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Logout */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 3,
                "&:hover": {
                  bgcolor: "rgba(239, 68, 68, 0.08)",
                  "& .MuiListItemText-primary": { color: "#ef4444" },
                  "& .MuiListItemIcon-root": { color: "#ef4444" },
                },
              }}
            >
              <ListItemIcon sx={{ color: "grey", minWidth: 40 }}>
                <LogOut size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 100,
                    color: "black",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* Bottom Kit Image */}
      <Box sx={{ p: 3, textAlign: "", bgcolor: "white" }}>
        {/* <Box
          component="img"
          src="https://material-kit-react.devias.io/assets/devias-kit-pro.png"
          alt="Pro features"
          sx={{ width: "100%", maxWidth: 200, height: "auto", mx: "auto" }}
        /> */}
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Mobile Header */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #eee",
            p: "5px 20px",
          }}
        >
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle}>
            <Box sx={{ display: 'flex' }}><Menu color="black" size={24} /></Box>
          </IconButton>
          <Avatar
            src={ProfileImg}
            sx={{ width: 29, height: 29, cursor: "pointer" }}
            onClick={handleAvatarClick}
          />
        </Box>
      )}

      {/* Avatar Menu */}
      <MuiMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate("/profile");
          }}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
        >
          Logout
        </MenuItem>
      </MuiMenu>

      {/* Drawers */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            bgcolor: "#fff",
            borderRight: "1px solid #eee",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            bgcolor: "#fff",
            border: "none",
            borderRight: "1px solid #eee",
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
